import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { Notification, NOTIFICATION_TYPE } from '@yandex-int/k-common/typings';
import { Headphones, Messenger } from 'education-icons';
import { Text } from 'platform-components';
import { TEXT_SIZE } from 'platform-components/src/components/constants';
import * as actions from 'platform-components/src/components/notifications/actions';
import { getNotificationsLoadingStatus } from 'platform-components/src/components/notifications/selectors';
import { InjectedProps as WithRouterPathProps, withRouterPath } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';

import { getFirstNotification } from 'common.components/notifications/selectors';

import { getIsAuthenticated } from 'platform-components/src/components/user/selectors';
import CommonNotificationMessage from './__common/changes-notification__common';
import NotificationWithAction from './__with-action/changes-notification__with-action';
import NotificationWithHeader from './__with-header/changes-notification__with-header';
import { getShouldHideNotification } from './selectors';
import { NOTIFICATION_THEME } from './typings';

import './changes-notification.scss';

interface StoreProps {
    notification: Notification | null;
    loadingStatus: LOADING_STATUSES;
    shouldHideNotification: boolean;
    isAuthenticated: boolean;
}

interface DispatchProps {
    getNotificationsRequest: typeof actions.getNotificationsRequest;
    updateNotificationsRequest: typeof actions.updateNotificationsRequest;
}

interface HocProps extends WithRouterPathProps, WrappedComponentProps {}

interface Props extends HocProps, StoreProps, DispatchProps {}

export class ChangesNotification extends React.Component<Props> {
    static contextTypes = {
        prefix: PropTypes.string.isRequired,
        staticUrls: PropTypes.object.isRequired,
    };

    constructor(props: Props) {
        super(props);

        const { loadingStatus, getNotificationsRequest, isAuthenticated } = props;

        if (loadingStatus === LOADING_STATUSES.UNSENT && !isSSR && isAuthenticated) {
            getNotificationsRequest();
        }
    }

    componentDidUpdate() {
        const { notification, updateNotificationsRequest, loadingStatus, shouldHideNotification } = this.props;

        if (notification && shouldHideNotification && loadingStatus === LOADING_STATUSES.SUCCESS) {
            updateNotificationsRequest({
                ids: [notification.id],
                isViewed: true,
            });
        }
    }
    context!: React.ContextType<
        React.Context<{
            staticUrls: Record<string, string>;
            prefix: string;
        }>
    >;

    handleAcceptClick = () => {
        const { updateNotificationsRequest, notification } = this.props;

        if (notification) {
            updateNotificationsRequest({
                ids: [notification.id],
                isViewed: true,
            });
        }
    };

    // eslint-disable-next-line complexity
    render() {
        const { getRouterPath, notification, intl, shouldHideNotification } = this.props;
        const {
            prefix,
            staticUrls: { labClassPrint },
        } = this.context;

        if (!notification || shouldHideNotification) {
            return null;
        }

        switch (notification.type_slug) {
            case NOTIFICATION_TYPE.SCHOOL_CODE_UPDATED:
                const { team_id: classId, level, name } = notification.data;

                return (
                    <NotificationWithAction
                        buttonTextId="changesNotification.printPasswords"
                        linkProps={{
                            isRouter: true,
                            to: `${prefix}${getRouterPath(labClassPrint, { classId })}`,
                        }}
                        messageTextId="changesNotification.schoolCodeWasChangedWithClass"
                        messageTextValues={{ level, name }}
                        onClose={this.handleAcceptClick}
                    />
                );
            case NOTIFICATION_TYPE.TEAM_UPDATED:
                return (
                    <CommonNotificationMessage onClose={this.handleAcceptClick} theme={NOTIFICATION_THEME.WARNING}>
                        <Text
                            id="changesNotification.teamUpdated"
                            size={TEXT_SIZE.XL}
                            values={{
                                old_level: notification.data.level,
                                old_name: notification.data.name,
                                ...notification.data,
                            }}
                        />
                    </CommonNotificationMessage>
                );
            case NOTIFICATION_TYPE.TEAM_DELETED:
                return (
                    <CommonNotificationMessage onClose={this.handleAcceptClick} theme={NOTIFICATION_THEME.WARNING}>
                        <Text id="changesNotification.teamDeleted" size={TEXT_SIZE.XL} values={notification.data} />
                    </CommonNotificationMessage>
                );
            case NOTIFICATION_TYPE.TEAM_ARCHIVED:
                return (
                    <CommonNotificationMessage onClose={this.handleAcceptClick} theme={NOTIFICATION_THEME.WARNING}>
                        <Text id="changesNotification.teamArchived" size={TEXT_SIZE.XL} values={notification.data} />
                    </CommonNotificationMessage>
                );
            case NOTIFICATION_TYPE.TEAM_UNARCHIVED:
                return (
                    <CommonNotificationMessage onClose={this.handleAcceptClick} theme={NOTIFICATION_THEME.SUCCESS}>
                        <Text id="changesNotification.teamUnarchived" size={TEXT_SIZE.XL} values={notification.data} />
                    </CommonNotificationMessage>
                );
            case NOTIFICATION_TYPE.TEAM_ACCESS_REVOKED:
                return (
                    <CommonNotificationMessage onClose={this.handleAcceptClick} theme={NOTIFICATION_THEME.WARNING}>
                        <Text
                            id="changesNotification.teamAccessRevoked"
                            size={TEXT_SIZE.XL}
                            values={notification.data}
                        />
                    </CommonNotificationMessage>
                );
            case NOTIFICATION_TYPE.MESSENGER_CLOSING:
                return (
                    <NotificationWithHeader
                        buttonTextId="changesNotification.messengerIsClosing.action"
                        headerTextId="changesNotification.messengerIsClosing.header"
                        icon={<Messenger />}
                        linkProps={{
                            isRouter: false,
                            target: '_blank',
                            to: intl.formatMessage({ id: 'changesNotification.messengerIsClosing.link' }),
                        }}
                        messageTextId="changesNotification.messengerIsClosing.text"
                        onClose={this.handleAcceptClick}
                        theme={NOTIFICATION_THEME.INFO}
                    />
                );
            case NOTIFICATION_TYPE.MESSENGER_CLOSED:
                return (
                    <NotificationWithHeader
                        buttonTextId="changesNotification.messengerIsClosed.action"
                        headerTextId="changesNotification.messengerIsClosed.header"
                        icon={<Messenger />}
                        linkProps={{
                            isRouter: false,
                            target: '_blank',
                            to: intl.formatMessage({ id: 'changesNotification.messengerIsClosed.link' }),
                        }}
                        messageTextId="changesNotification.messengerIsClosed.text"
                        onClose={this.handleAcceptClick}
                        theme={NOTIFICATION_THEME.INFO}
                    />
                );
            case NOTIFICATION_TYPE.WEBINAR_INVITATION:
                return (
                    <NotificationWithHeader
                        buttonTextId="changesNotification.webinarInvitation.action"
                        headerTextId="changesNotification.webinarInvitation.header"
                        icon={<Headphones />}
                        linkProps={{
                            isRouter: false,
                            target: '_blank',
                            to: intl.formatMessage({ id: 'changesNotification.webinarInvitation.link' }),
                        }}
                        messageTextId="changesNotification.webinarInvitation.text"
                        onClose={this.handleAcceptClick}
                        theme={NOTIFICATION_THEME.INFO}
                    />
                );
            default:
                return null;
        }
    }
}

const mapStateToProps = (storeState: any): StoreProps => ({
    notification: getFirstNotification(storeState),
    loadingStatus: getNotificationsLoadingStatus(storeState),
    shouldHideNotification: getShouldHideNotification(storeState),
    isAuthenticated: getIsAuthenticated(storeState),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps =>
    bindActionCreators(
        {
            getNotificationsRequest: actions.getNotificationsRequest,
            updateNotificationsRequest: actions.updateNotificationsRequest,
        },
        dispatch
    );

const wrapped = withRouterPath(injectIntl(connect(mapStateToProps, mapDispatchToProps)(ChangesNotification)));
wrapped.displayName = 'ChangesNotification';

export default wrapped;
